const MATRIX_SIZE = 384;
const WORKGROUP_SIZE = 8;
const MEASURED_RUNS = 3;

export async function inspectAndBenchmark({ onEvent = () => {} } = {}) {
  if (!globalThis.isSecureContext) {
    return unsupported("WebGPU requires HTTPS or localhost.");
  }
  if (!navigator.gpu) {
    return unsupported("navigator.gpu is unavailable. Try a current Chromium-based browser with WebGPU enabled.");
  }

  onEvent("Requesting a high-performance WebGPU adapter.");
  const adapter = await navigator.gpu.requestAdapter({ powerPreference: "high-performance" });
  if (!adapter) return unsupported("The browser could not provide a WebGPU adapter.");

  const adapterInfo = await readAdapterInfo(adapter);
  const features = [...adapter.features].sort();
  const limits = pickLimits(adapter.limits);
  const requiredFeatures = features.includes("shader-f16") ? ["shader-f16"] : [];

  let device;
  try {
    device = await adapter.requestDevice({ requiredFeatures });
  } catch (error) {
    return {
      capability: {
        webgpuAvailable: true,
        adapter: adapterInfo,
        features,
        limits,
        deviceError: errorMessage(error)
      },
      benchmark: { valid: false, error: errorMessage(error) }
    };
  }

  let intentionallyDestroyed = false;
  device.lost.then((info) => {
    if (!intentionallyDestroyed) onEvent(`WebGPU device lost: ${info.message || info.reason}`);
  });
  onEvent(`Adapter ready: ${adapterInfo.description}.`);

  try {
    const benchmark = await runMatrixBenchmark(device, onEvent);
    return {
      capability: { webgpuAvailable: true, adapter: adapterInfo, features, limits },
      benchmark
    };
  } catch (error) {
    return {
      capability: { webgpuAvailable: true, adapter: adapterInfo, features, limits },
      benchmark: { valid: false, error: errorMessage(error), matrixSize: MATRIX_SIZE }
    };
  } finally {
    intentionallyDestroyed = true;
    device.destroy();
  }
}

async function runMatrixBenchmark(device, onEvent) {
  const elementCount = MATRIX_SIZE * MATRIX_SIZE;
  const byteLength = elementCount * Float32Array.BYTES_PER_ELEMENT;
  const valuesA = new Float32Array(elementCount);
  const valuesB = new Float32Array(elementCount);
  for (let index = 0; index < elementCount; index += 1) {
    valuesA[index] = ((index * 13) % 97) / 97;
    valuesB[index] = ((index * 7 + 3) % 89) / 89;
  }

  const usage = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST;
  const bufferA = device.createBuffer({ size: byteLength, usage, label: "matrix-a" });
  const bufferB = device.createBuffer({ size: byteLength, usage, label: "matrix-b" });
  const bufferC = device.createBuffer({
    size: byteLength,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    label: "matrix-output"
  });
  const readback = device.createBuffer({
    size: 16,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    label: "matrix-readback"
  });
  const uniform = device.createBuffer({
    size: 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: "matrix-size"
  });

  device.queue.writeBuffer(bufferA, 0, valuesA);
  device.queue.writeBuffer(bufferB, 0, valuesB);
  device.queue.writeBuffer(uniform, 0, new Uint32Array([MATRIX_SIZE, 0, 0, 0]));

  const module = device.createShaderModule({ code: shaderSource(), label: "matmul-proxy" });
  const pipeline = device.createComputePipeline({
    layout: "auto",
    compute: { module, entryPoint: "main" },
    label: "matmul-proxy"
  });
  const bindGroup = device.createBindGroup({
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: bufferA } },
      { binding: 1, resource: { buffer: bufferB } },
      { binding: 2, resource: { buffer: bufferC } },
      { binding: 3, resource: { buffer: uniform } }
    ]
  });

  const encodePass = (copySample = false) => {
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(Math.ceil(MATRIX_SIZE / WORKGROUP_SIZE), Math.ceil(MATRIX_SIZE / WORKGROUP_SIZE));
    pass.end();
    if (copySample) encoder.copyBufferToBuffer(bufferC, 0, readback, 0, 16);
    return encoder.finish();
  };

  onEvent(`Warming a ${MATRIX_SIZE} × ${MATRIX_SIZE} matrix shader.`);
  device.queue.submit([encodePass()]);
  await device.queue.onSubmittedWorkDone();

  const durations = [];
  for (let run = 0; run < MEASURED_RUNS; run += 1) {
    const startedAt = performance.now();
    device.queue.submit([encodePass(run === MEASURED_RUNS - 1)]);
    await device.queue.onSubmittedWorkDone();
    durations.push(performance.now() - startedAt);
  }

  await readback.mapAsync(GPUMapMode.READ);
  const sample = [...new Float32Array(readback.getMappedRange().slice(0))];
  readback.unmap();
  const valid = sample.every(Number.isFinite) && sample.some((value) => value !== 0);
  const sorted = [...durations].sort((a, b) => a - b);
  const medianMs = sorted[Math.floor(sorted.length / 2)];
  const operations = 2 * MATRIX_SIZE ** 3;
  const gflops = operations / (medianMs / 1000) / 1e9;

  for (const buffer of [bufferA, bufferB, bufferC, readback, uniform]) buffer.destroy();
  if (!valid) throw new Error("The compute shader returned an invalid sample.");
  onEvent(`Compute proxy complete: ${gflops.toFixed(1)} GFLOP/s median.`);

  return {
    valid: true,
    kind: "f32-matrix-multiply-proxy",
    matrixSize: MATRIX_SIZE,
    measuredRuns: MEASURED_RUNS,
    durationsMs: durations.map((value) => Number(value.toFixed(2))),
    medianMs: Number(medianMs.toFixed(2)),
    gflops: Number(gflops.toFixed(2)),
    sample: sample.map((value) => Number(value.toFixed(4))),
    caveat: "Synthetic F32 shader throughput is a screening proxy, not a FLUX render-time estimate."
  };
}

function shaderSource() {
  return `
    struct Params { size: u32, _pad0: u32, _pad1: u32, _pad2: u32 };
    @group(0) @binding(0) var<storage, read> a: array<f32>;
    @group(0) @binding(1) var<storage, read> b: array<f32>;
    @group(0) @binding(2) var<storage, read_write> c: array<f32>;
    @group(0) @binding(3) var<uniform> params: Params;

    @compute @workgroup_size(${WORKGROUP_SIZE}, ${WORKGROUP_SIZE})
    fn main(@builtin(global_invocation_id) id: vec3<u32>) {
      let row = id.y;
      let col = id.x;
      if (row >= params.size || col >= params.size) { return; }
      var sum = 0.0;
      for (var k = 0u; k < params.size; k = k + 1u) {
        sum = sum + a[row * params.size + k] * b[k * params.size + col];
      }
      c[row * params.size + col] = sum;
    }
  `;
}

async function readAdapterInfo(adapter) {
  let info = adapter.info;
  if (!info && typeof adapter.requestAdapterInfo === "function") {
    try { info = await adapter.requestAdapterInfo(); } catch { /* privacy-reduced adapter info is valid */ }
  }
  const parts = [info?.vendor, info?.architecture, info?.device].filter(Boolean);
  return {
    vendor: info?.vendor || "unknown",
    architecture: info?.architecture || "unknown",
    device: info?.device || "unknown",
    description: info?.description || parts.join(" · ") || "WebGPU adapter"
  };
}

function pickLimits(limits) {
  const names = [
    "maxBufferSize",
    "maxStorageBufferBindingSize",
    "maxComputeInvocationsPerWorkgroup",
    "maxComputeWorkgroupStorageSize",
    "maxComputeWorkgroupsPerDimension"
  ];
  return Object.fromEntries(names.map((name) => [name, Number(limits[name] || 0)]));
}

function unsupported(reason) {
  return {
    capability: { webgpuAvailable: false, reason, adapter: null, features: [], limits: {} },
    benchmark: { valid: false, error: reason }
  };
}

function errorMessage(error) {
  return error instanceof Error ? error.message : String(error);
}
